import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { GatorMarkCircle } from '../shared/GatorMarkCircle.js';
import { cn } from '../../lib/utils.js';

type TransitionVariant =
  | { kind: 'stage'; stageNumber: number; totalStages: number; skipped?: boolean }
  | { kind: 'language' };

/**
 * The moment after the LAST answer of a stage (or of the language check):
 * a full-screen beat that first celebrates ("Stage 2 complete!"), shows where
 * the user is on the interview path, and hands over to the Gator loader while
 * the completion request and next-stage navigation happen underneath.
 *
 * Same backdrop and loader anatomy as AppLoaderOverlay — this is a themed
 * variant of the one app loader, not a second loader design.
 */
export function StageTransitionOverlay(props: TransitionVariant) {
  const { t } = useTranslation();

  const isFinalStage = props.kind === 'stage' && props.stageNumber >= props.totalStages;
  const isSkipped = props.kind === 'stage' && props.skipped === true;

  const headline =
    props.kind === 'language'
      ? t('transition.languageDone', { defaultValue: 'Language check complete!' })
      : isSkipped
        ? t('transition.stageSkipped', { defaultValue: 'Stage {{n}} skipped', n: props.stageNumber })
        : isFinalStage
          ? t('transition.interviewDone', { defaultValue: 'Interview complete!' })
          : t('transition.stageDone', { defaultValue: 'Stage {{n}} complete!', n: props.stageNumber });

  const message =
    props.kind === 'language'
      ? t('transition.languageNext', { defaultValue: 'Saving your answers — Stage 1 is next…' })
      : isSkipped
        ? isFinalStage
          ? t('transition.skippedFinalNext', { defaultValue: 'No answers here — no feedback for this stage. Wrapping up…' })
          : t('transition.skippedNext', {
              defaultValue: 'No answers here — no feedback for this stage. Preparing Stage {{n}}…',
              n: props.stageNumber + 1,
            })
        : isFinalStage
          ? t('transition.finalNext', { defaultValue: 'Handing everything to Gator for your final report…' })
          : t('transition.stageNext', { defaultValue: 'Preparing Stage {{n}}…', n: props.stageNumber + 1 });

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-7 bg-ig-bg-overlay px-6 backdrop-blur-sm"
    >
      {/* Success / skipped burst */}
      <div className="relative flex h-20 w-20 items-center justify-center motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-500">
        <span
          aria-hidden
          className={cn(
            'absolute inset-0 rounded-full motion-safe:animate-ping motion-safe:[animation-duration:1.6s]',
            isSkipped ? 'bg-ig-warning/15' : 'bg-ig-success/15',
          )}
        />
        <span
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full',
            isSkipped ? 'bg-ig-warning/15' : 'bg-ig-success/15',
          )}
        >
          {isSkipped ? (
            <Icon name="arrow-right" className="h-9 w-9 text-ig-warning" strokeWidth={1.75} />
          ) : (
            <Icon name="check-circle-2" className="h-9 w-9 text-ig-success" strokeWidth={1.75} />
          )}
        </span>
      </div>

      <h2 className="text-center font-display text-[34px] font-light leading-tight text-ig-text-primary motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-500 md:text-[44px]">
        {headline}
      </h2>

      {/* Interview path: which stages are behind you */}
      {props.kind === 'stage' ? (
        <div className="flex items-center gap-2" aria-hidden>
          {Array.from({ length: props.totalStages }, (_, index) => {
            const stage = index + 1;
            const done = stage <= props.stageNumber;
            const justDone = stage === props.stageNumber;
            const skippedNode = justDone && isSkipped;
            return (
              <div key={stage} className="flex items-center gap-2">
                {stage > 1 ? (
                  <span className={cn('h-px w-8', stage <= props.stageNumber ? 'bg-ig-accent' : 'bg-ig-border-strong')} />
                ) : null}
                <span
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border font-sans text-[12px] font-semibold transition-colors',
                    skippedNode
                      ? 'border-ig-warning bg-ig-warning/15 text-ig-warning'
                      : done
                        ? 'border-ig-accent bg-ig-accent text-[var(--ig-accent-text)]'
                        : 'border-ig-border-strong text-ig-text-tertiary',
                    justDone && 'motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:duration-700',
                  )}
                >
                  {skippedNode ? (
                    <Icon name="arrow-right" className="h-4 w-4" />
                  ) : done ? (
                    <Icon name="check" className="h-4 w-4" />
                  ) : (
                    stage
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Hand-off to the one app loader */}
      <div className="flex items-center gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-700 motion-safe:[animation-delay:300ms] motion-safe:[animation-fill-mode:both]">
        <div className="relative flex h-10 w-10 items-center justify-center">
          <span
            aria-hidden
            className="absolute inset-0 rounded-full border-2 border-ig-accent/15 border-t-ig-accent motion-safe:animate-spin motion-safe:[animation-duration:1.1s]"
          />
          <GatorMarkCircle className="relative h-6 w-6" alt="" />
        </div>
        <p className="max-w-[340px] font-display text-[15px] italic leading-[1.6] text-ig-text-secondary">{message}</p>
      </div>
    </div>
  );
}
